import { HomeReview, ReviewContactDetails } from '../types';
import { offlineDatabase, OfflineReview } from './offlineDatabase';

export interface OfflineReviewRecordSyncDatabase {
  getReview?: typeof offlineDatabase.getReview;
  getReviews: typeof offlineDatabase.getReviews;
  saveIdMapping: typeof offlineDatabase.saveIdMapping;
  updateReview: typeof offlineDatabase.updateReview;
  updateReviewIfUnchanged?: typeof offlineDatabase.updateReviewIfUnchanged;
  markOfflineMutationsSyncedForReview?: typeof offlineDatabase.markOfflineMutationsSyncedForReview;
}

export interface OfflineReviewRecordSyncReviewApi {
  createReview(
    propertyId: number,
    templateId: number,
    address?: string,
    detailsOrOptions?: ReviewContactDetails | { forceOnline?: boolean },
    options?: { forceOnline?: boolean }
  ): Promise<HomeReview>;
  updateReviewNotes(reviewId: number, notes: string): Promise<unknown>;
  updateReviewOnDemandProjects(reviewId: number, onDemandProjects: string): Promise<unknown>;
  updateReviewDetails(reviewId: number, details: ReviewContactDetails): Promise<unknown>;
}

interface OfflineReviewRecordSyncServiceOptions {
  database: OfflineReviewRecordSyncDatabase;
  reviewApi: OfflineReviewRecordSyncReviewApi;
  fetchReview(reviewId: number): Promise<Partial<HomeReview> & Record<string, any>>;
  getRealId(tempId: string): Promise<number | undefined>;
  mapTempIdToRealId(tempId: string, realId: number): Promise<void>;
  saveAllDrafts(): Promise<void>;
}

export class OfflineReviewRecordSyncService {
  constructor(private readonly options: OfflineReviewRecordSyncServiceOptions) {}

  async syncPendingReviews(isOnline: boolean): Promise<void> {
    if (!isOnline) {
      return;
    }

    const { database } = this.options;

    try {
      await this.options.saveAllDrafts();

      const pendingReviews = await database.getReviews({ syncStatus: 'pending' });
      const failedReviews = await database.getReviews({ syncStatus: 'failed' });
      const allReviewsToSync = [...pendingReviews, ...failedReviews];

      for (const review of allReviewsToSync) {
        try {
          if (review.syncStatus === 'failed') {
            await database.updateReview(review.id, {
              syncStatus: 'pending',
              syncError: undefined,
            });
            review.syncStatus = 'pending';
            review.syncError = undefined;
          }

          await this.syncSingleReview(review);
        } catch (error) {
          console.warn('[OfflineReviewRecordSyncService] Error syncing review:', review.id, error);
          await database.updateReview(review.id, {
            syncError: error instanceof Error ? error.message : 'Unknown sync error',
            lastSyncAttempt: new Date(),
          });
        }
      }
    } catch (error) {
      console.warn('[OfflineReviewRecordSyncService] Error syncing pending reviews:', error);
    }
  }

  async syncSingleReview(offlineReview: OfflineReview): Promise<void> {
    const { database, reviewApi } = this.options;
    const baselineRevision = offlineReview.localRevision ?? 0;

    await database.updateReview(offlineReview.id, {
      syncStatus: 'syncing',
      lastSyncAttempt: new Date(),
    });

    try {
      const latestBeforeUpload = await this.getLatestReview(offlineReview) || offlineReview;
      const reviewToUpload = this.hasLocalMutationAdvanced(offlineReview, latestBeforeUpload)
        ? latestBeforeUpload
        : offlineReview;
      let serverReviewId: number;

      if (reviewToUpload.tempId) {
        const existingMapping = await this.options.getRealId(reviewToUpload.tempId);

        if (existingMapping != null) {
          serverReviewId = existingMapping;
        } else {
          const createdReview = reviewToUpload.memberName
            ? await reviewApi.createReview(
              reviewToUpload.propertyId,
              reviewToUpload.templateId,
              reviewToUpload.address,
              {
                memberName: reviewToUpload.memberName,
                memberEmail: reviewToUpload.memberEmail,
                homeManagerName: reviewToUpload.homeManagerName,
                homeManagerEmail: reviewToUpload.homeManagerEmail,
              },
              { forceOnline: true }
            )
            : await reviewApi.createReview(
              reviewToUpload.propertyId,
              reviewToUpload.templateId,
              reviewToUpload.address,
              { forceOnline: true }
            );

          await database.saveIdMapping(reviewToUpload.tempId, createdReview.id, 'review');
          await this.options.mapTempIdToRealId(reviewToUpload.tempId, createdReview.id);
          serverReviewId = createdReview.id;
        }
      } else {
        serverReviewId = reviewToUpload.id;
      }

      let uploadError: unknown = null;

      if (reviewToUpload.memberName) {
        try {
          await reviewApi.updateReviewDetails(serverReviewId, {
            memberName: reviewToUpload.memberName,
            memberEmail: reviewToUpload.memberEmail,
            homeManagerName: reviewToUpload.homeManagerName,
            homeManagerEmail: reviewToUpload.homeManagerEmail,
          });
        } catch (detailsError) {
          console.warn('[OfflineReviewRecordSyncService] Failed to sync review details:', detailsError);
          uploadError = detailsError;
        }
      }

      if (reviewToUpload.notes !== undefined) {
        try {
          await reviewApi.updateReviewNotes(serverReviewId, reviewToUpload.notes || '');
        } catch (notesError) {
          console.warn('[OfflineReviewRecordSyncService] Failed to sync notes:', notesError);
          uploadError = notesError;
        }
      }

      if (reviewToUpload.onDemandProjects !== undefined) {
        try {
          await reviewApi.updateReviewOnDemandProjects(
            serverReviewId,
            reviewToUpload.onDemandProjects || ''
          );
        } catch (onDemandProjectsError) {
          console.warn(
            '[OfflineReviewRecordSyncService] Failed to sync On Demand Projects:',
            onDemandProjectsError
          );
          uploadError = onDemandProjectsError;
        }
      }

      if (uploadError) {
        throw uploadError;
      }

      await database.updateReview(offlineReview.id, {
        syncStatus: 'pending',
        syncError: undefined,
      });

      try {
        const freshReview = await this.options.fetchReview(serverReviewId);

        const didApplyFreshReview = await this.applyReviewSyncResultIfUnchanged(reviewToUpload, {
          status: freshReview.status,
          startedAt: freshReview.startedAt ? new Date(freshReview.startedAt) : undefined,
          completedAt: freshReview.completedAt ? new Date(freshReview.completedAt) : undefined,
          submittedAt: freshReview.submittedAt ? new Date(freshReview.submittedAt) : undefined,
          notes: freshReview.notes,
          onDemandProjects: freshReview.onDemandProjects,
          address: freshReview.address || freshReview.property?.address,
          memberName: freshReview.memberName,
          memberEmail: freshReview.memberEmail,
          homeManagerName: freshReview.homeManagerName,
          homeManagerEmail: freshReview.homeManagerEmail,
          propertyId: freshReview.propertyId,
          lastServerSeenAt: freshReview.updatedAt ? new Date(freshReview.updatedAt) : new Date(),
          serverRevision: (reviewToUpload.serverRevision ?? 0) + 1,
          conflictStatus: 'none',
        });
        if (!didApplyFreshReview) {
          console.warn('[OfflineReviewRecordSyncService] Ignored stale review sync result because local review changed during sync:', {
            reviewId: offlineReview.id,
            baselineRevision,
            uploadRevision: reviewToUpload.localRevision,
          });
        } else {
          await database.markOfflineMutationsSyncedForReview?.(offlineReview.id);
        }
      } catch (fetchError) {
        console.error('[OfflineReviewRecordSyncService] Failed to fetch fresh review data:', fetchError);
      }
    } catch (error) {
      await database.updateReview(offlineReview.id, {
        syncStatus: 'failed',
        syncError: error instanceof Error ? error.message : 'Unknown sync error',
      });
      throw error;
    }
  }

  private async applyReviewSyncResultIfUnchanged(
    baseline: OfflineReview,
    updates: Partial<OfflineReview>
  ): Promise<boolean> {
    const expectedRevision = baseline.localRevision ?? 0;

    if (this.options.database.updateReviewIfUnchanged) {
      return this.options.database.updateReviewIfUnchanged(
        baseline.id,
        expectedRevision,
        updates
      );
    }

    const latest = await this.getLatestReview(baseline);
    if (latest && this.hasLocalMutationAdvanced(baseline, latest)) {
      return false;
    }

    await this.options.database.updateReview(baseline.id, updates);
    return true;
  }

  private async getLatestReview(review: OfflineReview): Promise<OfflineReview | null> {
    if (this.options.database.getReview) {
      return this.options.database.getReview(review.id);
    }

    const reviews = await this.options.database.getReviews({});
    return reviews.find(candidate => candidate.id === review.id) || null;
  }

  private hasLocalMutationAdvanced(
    baseline: OfflineReview,
    latest: OfflineReview
  ): boolean {
    const baselineRevision = baseline.localRevision ?? 0;
    const latestRevision = latest.localRevision ?? 0;
    if (latestRevision > baselineRevision) {
      return true;
    }

    const baselineMutationAt = this.timestampMs(baseline.lastLocalMutationAt || baseline.updatedAt);
    const latestMutationAt = this.timestampMs(latest.lastLocalMutationAt || latest.updatedAt);
    return baselineMutationAt != null &&
      latestMutationAt != null &&
      latestMutationAt > baselineMutationAt;
  }

  private timestampMs(value: unknown): number | null {
    if (!value) {
      return null;
    }

    const time = new Date(value as any).getTime();
    return Number.isFinite(time) ? time : null;
  }
}
